import * as React from 'react';

import { Grid, Spacer } from '@emburse/ui-core';

import { StripeCheckout } from '@ReactComponents/AdminSettings/Components/Billing/StripeCheckout';
import { VerifyStripeBank } from '@ReactComponents/AdminSettings/Components/Billing/VerifyStripeBank';
import { LoadingState } from '@ReactComponents/Shared/LoadingState';

import { setupIntentRequiresMicrodeposits } from '@ReactHooks/useStripeSubscriptionAndCheckout';

interface IStripeCheckoutSectionProps {
  stripeCheckoutSession: any;
  activeStripeSubscription: any;
  processingPaymentInfo: boolean;
  onUpdatePaymentMethod: (paymentMethodId: string) => void;
  setStripeCheckoutSession: (session: any) => void;
  cancelStripeCheckoutSession: () => void;
  showSubscribe?: boolean;
  onCancel?: () => void;
  minWidth?: string;
  onPaymentSuccess?: () => Promise<void>;
}

/**
 * Shared component for rendering Stripe checkout functionality.
 * Handles three states:
 * 1. Regular checkout (when no microdeposits required)
 * 2. Microdeposit verification (when bank account needs verification)
 * 3. Loading state (when initializing or processing)
 */
export const StripeCheckoutSection: React.FC<IStripeCheckoutSectionProps> = ({
  stripeCheckoutSession,
  activeStripeSubscription,
  processingPaymentInfo,
  onUpdatePaymentMethod,
  setStripeCheckoutSession,
  cancelStripeCheckoutSession,
  showSubscribe = true,
  onCancel = () => {},
  minWidth = '224px',
  onPaymentSuccess
}) => {
  return (
    <Grid item style={{ minWidth, width: '100%' }}>
      {stripeCheckoutSession && !setupIntentRequiresMicrodeposits(stripeCheckoutSession) && activeStripeSubscription && (
        <StripeCheckout
          showSubscribe={showSubscribe}
          subscription={activeStripeSubscription}
          checkoutSession={stripeCheckoutSession}
          onUpdatePaymentMethod={onUpdatePaymentMethod}
          onShowMicrodepositForm={setStripeCheckoutSession}
          onCancel={onCancel}
          onPaymentSuccess={onPaymentSuccess}
        />
      )}
      {stripeCheckoutSession && setupIntentRequiresMicrodeposits(stripeCheckoutSession) && activeStripeSubscription && (
        <>
          <Spacer direction="vertical" size={72} />
          <VerifyStripeBank
            verifyWithMicrodeposits={stripeCheckoutSession.next_action.verify_with_microdeposits}
            onCancel={cancelStripeCheckoutSession}
          />
        </>
      )}
      {(!stripeCheckoutSession || !activeStripeSubscription) && (
        <LoadingState caption={processingPaymentInfo ? 'Processing payment information' : 'Initializing checkout session'} />
      )}
    </Grid>
  );
};
